import { PublicUser } from '@uplift/api';
import React, { useMemo } from 'react';
import { ProviderMedicalRole } from '@uplift/types';
import { ProviderCardDesktop } from './desktop';
import { ProviderCardMobile } from './mobile';
import { useProviderLicenseTypesQuery, useProviderPublicMatchingProfile, useUserDisplayNameQuery } from '@uplift/queries';
import { useUpBreakpoint } from '@uplift/hooks';

export type ProviderCardProps = {
  matchId: string;
  provider: PublicUser;
  square?: boolean;
};

export type ProviderCardViewProps = {
  userName: string;
  providerType: string;
  provider: ProviderCardProps['provider'];
  matchId: string;
  square?: boolean;
};

export const ProviderCard: React.FC<ProviderCardProps> = ({ matchId, provider, square }) => {
  const isSmUp = useUpBreakpoint('sm');
  const { data: publicProfile } = useProviderPublicMatchingProfile(provider.id, { enabled: !!provider?.id, suspense: true });
  const mentalHealthRoles = React.useMemo(() => publicProfile?.licenses?.filter(l => l.role.suffix)?.map(l => l.role.suffix) ?? [], [ publicProfile ]);
  const { data, isLoading } = useProviderLicenseTypesQuery(provider?.id, {
    enabled: !!provider.id,
  });

  const providerType = useMemo(() => {
    if (isLoading) {
      return;
    }
    if (data?.includes('psychologist')) {
      return 'psychologist';
    }
    if (mentalHealthRoles.includes('PMHNP')) {
      return 'pmhnp';
    } else if (provider.medicalRole) {
      return provider.medicalRole;
    }
    return ProviderMedicalRole.therapist;
  }, [ mentalHealthRoles, data, isLoading, provider.medicalRole ]);

  const { data: userName } = useUserDisplayNameQuery(provider.id, { enabled: !!provider?.id, suspense: true });

  const displayName = useMemo(() => {
    if (providerType === ProviderMedicalRole.therapist) {
      return userName?.full;
    }
    return userName?.fullWithAffixes;
  }, [ providerType, userName?.full, userName?.fullWithAffixes ]);

  return isSmUp ? (
    <ProviderCardDesktop
      userName={displayName}
      provider={provider}
      square={square}
      providerType={providerType}
      matchId={matchId}
    />
  ) : (
    <ProviderCardMobile
      userName={displayName}
      provider={provider}
      square={square}
      providerType={providerType}
      matchId={matchId}
    />
  );
};
